import { useEffect, useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { X } from "lucide-react";
import { TbFileTypePdf } from "react-icons/tb";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import uploadFileToCloudinary from "../../../../utils/uploadFileToCloudinary";

const MiniPdfUploadSection = ({ floorDesign, onChange, maxSizeMB = 10 }) => {
  const fileInputRef = useRef(null);

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(floorDesign || "");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  //   Generic update helper (correct usage)
  const updatePdfUrl = (value) => {
    setPdfUrl(value);
    onChange?.(value);
  };

  //   Sync external floorDesign only when changed
  useEffect(() => {
    if (floorDesign && floorDesign !== pdfUrl) {
      updatePdfUrl(floorDesign);
    }
  }, [floorDesign]);

  //   Handle upload on file select
  useEffect(() => {
    const handleUpload = async () => {
      if (!pdfFile) return;

      try {
        setIsUploading(true);
        setUploadProgress(0);

        const { uploadPromise } = uploadFileToCloudinary(pdfFile, (p) =>
          setUploadProgress(p)
        );
        const result = await uploadPromise;

        updatePdfUrl(result.url);
        showSuccess("PDF uploaded successfully!");
      } catch (err) {
        showError(err.message || "Failed to upload PDF");
      } finally {
        setIsUploading(false);
      }
    };

    handleUpload();
  }, [pdfFile]);

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      showError("Only PDF files are allowed!");
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      showError(`File size exceeds ${maxSizeMB}MB`);
      return;
    }

    setPdfFile(file);
  };

  const handleRemovePdf = (e) => {
    e.stopPropagation();
    setPdfFile(null);
    updatePdfUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!pdfUrl) {
      showError("No PDF available to download");
      return;
    }

    if (isDownloading) return;
    setIsDownloading(true);

    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "FloorDesign.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSuccess("PDF downloaded successfully");
    } catch (error) {
      showError("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="w-full h-fit self-center">
      {!pdfUrl ? (
        isUploading ? (
          <div className="flex flex-col items-center gap-1 py-3 border border-primary rounded-lg">
            <AiOutlineLoading3Quarters className="animate-spin text-primary size-5" />
            <p className="text-[11px] text-text-muted">
              Uploading... {uploadProgress}%
            </p>
          </div>
        ) : (
          <>
            <label
              htmlFor="floorPdf"
              className="relative group w-full h-fit border border-primary text-center text-primary py-2 text-xs rounded-lg font-medium flex flex-col items-center justify-center hover:bg-primary/20 hover:text-primary gap-1 transition-all cursor-pointer"
            >
              Upload Floor Design
              <div className="flex gap-1 items-center">
                <FiUpload className="size-4" />
                <span>(PDF)</span>
              </div>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              id="floorPdf"
              accept="application/pdf"
              onChange={handlePdfUpload}
              className="hidden"
            />
          </>
        )
      ) : (
        <div
          onClick={pdfUrl ? handleDownload : undefined}
          className="relative group w-full h-fit bg-gray-50 border border-border rounded-lg py-2 px-3 text-xs font-medium flex flex-col items-center justify-center text-text-primary overflow-hidden cursor-pointer"
        >
          <button
            onClick={handleRemovePdf}
            className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>

          {isUploading ? (
            <div className="flex flex-col items-center gap-1">
              <AiOutlineLoading3Quarters className="animate-spin text-primary size-5" />
              <p className="text-[11px] text-text-muted">
                Uploading... {uploadProgress}%
              </p>
            </div>
          ) : (
            <>
              <TbFileTypePdf className="text-primary size-5 mb-1" />
              {isDownloading ? (
                <div className="flex items-center gap-1 text-[11px] text-text-muted">
                  <AiOutlineLoading3Quarters className="animate-spin size-3" />
                  Downloading...
                </div>
              ) : (
                <p className="truncate max-w-[90%] text-center hover:underline">
                  {pdfFile?.name || "FloorDesign.pdf"}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MiniPdfUploadSection;

// import { useEffect, useRef, useState } from "react";
// import { FiUpload } from "react-icons/fi";
// import { X } from "lucide-react";
// import { TbFileTypePdf } from "react-icons/tb";
// import { AiOutlineLoading3Quarters } from "react-icons/ai";
// import { showError, showSuccess } from "../../../../utils/toastUtils";
// import uploadFileToCloudinary from "../../../../utils/uploadFileToCloudinary";

// const MiniPdfUploadSection = ({ floorDesign, onChange, maxSizeMB = 10 }) => {
//   const fileInputRef = useRef(null);
//   const [pdfFile, setPdfFile] = useState(null);
//   const [pdfUrl, setPdfUrl] = useState(floorDesign || "");
//   const [isUploading, setIsUploading] = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(0);
//   const [isDownloading, setIsDownloading] = useState(false);

//   // console.log("floordesign,", floorDesign);
//   useEffect(() => {
//     if (floorDesign && floorDesign !== pdfUrl) {
//       setPdfUrl(floorDesign);
//     }
//   }, [floorDesign, pdfUrl]);

//   //  Notify parent whenever pdfUrl changes
//   useEffect(() => {
//     onChange?.(pdfUrl);
//   }, [pdfUrl]);

//   useEffect(() => {
//     const handleUpload = async () => {
//       if (!pdfFile) return;

//       try {
//         setIsUploading(true);
//         setUploadProgress(0);

//         const { uploadPromise } = uploadFileToCloudinary(pdfFile, (p) =>
//           setUploadProgress(p)
//         );
//         const result = await uploadPromise;

//         setIsUploading(false);
//         setPdfUrl(result.url);
//         showSuccess("PDF uploaded successfully!");
//       } catch (err) {
//         setIsUploading(false);
//         showError(err.message || "Failed to upload PDF");
//       }
//     };

//     handleUpload();
//   }, [pdfFile]);

//   const handlePdfUpload = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     if (file.type !== "application/pdf") {
//       showError("Only PDF files are allowed!");
//       return;
//     }

//     if (file.size > maxSizeMB * 1024 * 1024) {
//       showError(`File size exceeds ${maxSizeMB}MB`);
//       return;
//     }

//     setPdfFile(file);
//   };

//   const handleRemovePdf = (e) => {
//     e.stopPropagation();
//     setPdfFile(null);
//     setPdfUrl("");
//     onChange?.(""); // Notify parent that file is removed
//     if (fileInputRef.current) fileInputRef.current.value = "";
//   };

//   const handleDownload = async (e) => {
//     e.stopPropagation();
//     if (!pdfUrl) {
//       showError("No PDF available to download");
//       return;
//     }

//     if (isDownloading) return;
//     setIsDownloading(true);

//     try {
//       const response = await fetch(pdfUrl);
//       if (!response.ok) throw new Error("Failed to fetch file");

//       const blob = await response.blob();
//       const url = window.URL.createObjectURL(blob);

//       const link = document.createElement("a");
//       link.href = url;
//       link.download = "FloorDesign.pdf";
//       document.body.appendChild(link);
//       link.click();
//       link.remove();

//       showSuccess("PDF downloaded successfully");
//     } catch (error) {
//       showError("Failed to download file");
//     } finally {
//       setIsDownloading(false);
//     }
//   };

//   return (
//     <div className="w-full h-fit self-center">
//       {!pdfUrl ? (
//         <>
//           <label
//             htmlFor="floorPdf"
//             className="relative group w-full h-fit border border-primary text-center text-primary py-2 text-xs rounded-lg font-medium flex flex-col items-center justify-center hover:bg-primary/20 hover:text-primary gap-1 transition-all cursor-pointer"
//           >
//             Upload Floor Design
//             <div className="flex gap-1 items-center">
//               <FiUpload className="size-4" />
//               <span>(PDF)</span>
//             </div>
//           </label>
//           <input
//             ref={fileInputRef}
//             type="file"
//             id="floorPdf"
//             accept="application/pdf"
//             onChange={handlePdfUpload}
//             className="hidden"
//           />
//         </>
//       ) : (
//         <div
//           onClick={pdfUrl ? handleDownload : undefined}
//           className="relative group w-full h-fit bg-gray-50 border border-border rounded-lg py-2 px-3 text-xs font-medium flex flex-col items-center justify-center text-text-primary overflow-hidden cursor-pointer"
//         >
//           {/* Remove Button */}
//           <button
//             onClick={handleRemovePdf}
//             className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-600/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
//           >
//             <X className="w-3.5 h-3.5 text-white" />
//           </button>

//           {/* Uploading Spinner */}
//           {isUploading ? (
//             <div className="flex flex-col items-center gap-1">
//               <AiOutlineLoading3Quarters className="animate-spin text-primary size-5" />
//               <p className="text-[11px] text-text-muted">
//                 Uploading... {uploadProgress}%
//               </p>
//             </div>
//           ) : (
//             <>
//               <TbFileTypePdf className="text-primary size-5 mb-1" />
//               {isDownloading ? (
//                 <div className="flex items-center gap-1 text-[11px] text-text-muted">
//                   <AiOutlineLoading3Quarters className="animate-spin size-3" />
//                   Downloading...
//                 </div>
//               ) : (
//                 <p className="truncate max-w-[90%] text-center hover:underline">
//                   {pdfFile?.name || "FloorDesign.pdf"}
//                 </p>
//               )}
//             </>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// export default MiniPdfUploadSection;
